// Audit (admin only), the day-7 audit (phase 3, docs/qa-observer.md). Three parts,
// all read live from the auditor's own API through CXHub's proxy:
//   - Agreement with human rulings: per week, the share of ruled contacts
//     where the auditor's day-7 answer equals the person's, with the sample
//     size (spec QL-9). Each area's tier says how many contacts it samples.
//   - Rule on this week's sample: blind. The auditor's answer on a contact
//     shows only after you submit your ruling, and a ruling is locked once
//     submitted. Fixed choices only, no free text. Open the contact in CXHub
//     to read it.
//   - The newest audit records. A sampled contact you have not ruled on is
//     hidden from you until you do.
// The rubric, the tiers and the thresholds are placeholders until CS
// management sets them (spec QL-10). No rep is named or scored here: "the
// rep" is a category on the audit record.

import { useEffect, useState } from 'react'
import {
  Alert, Button, Checkbox, CircularProgress, FormControlLabel, Link, MenuItem, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material'
import { fetchQaObserver, postQaObserverRuling } from './api.js'
import { convNumberPath } from './contactPath.js'
import { SOLVED_LABEL, PART_LABEL, EXCEPTION_LABEL, rulingRequest, rulingErrorMessage, agreementLine, pct } from './qaRulings.js'

function when(v) {
  return v ? new Date(v).toLocaleString() : '—'
}

function Agreement() {
  const [data, setData] = useState(null)
  const [error, setError] = useState(null)
  useEffect(() => {
    fetchQaObserver('rulings/agreement', { weeks: 8 }).then(setData).catch((e) => setError(e.message))
  }, [])
  if (error) return <Alert severity="error">Could not load the agreement: {error}</Alert>
  if (!data) return <CircularProgress size={24} />
  const latest = data.weeks?.[0] ?? null
  return (
    <Stack spacing={1}>
      <Typography variant="subtitle1">Agreement with human rulings</Typography>
      <Typography variant="h5">{latest ? `${agreementLine(latest)}, week of ${latest.week}` : 'No rulings yet.'}</Typography>
      <Typography variant="body2" color="text.secondary">
        A ruling counts unless it is an exception or the person could not tell. An auditor &quot;unknown&quot; against a yes or a no
        counts as a disagreement. {data.placeholder}
      </Typography>
      {data.weeks?.length > 0 && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Week of</TableCell><TableCell align="right">Agreement</TableCell><TableCell align="right">Counted</TableCell>
              <TableCell align="right">Auditor unknown</TableCell><TableCell align="right">Exceptions</TableCell>
              <TableCell align="right">Can&apos;t tell</TableCell><TableCell align="right">Same failed part</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.weeks.map((w) => (
              <TableRow key={w.week}>
                <TableCell>{w.week}</TableCell><TableCell align="right">{pct(w.agreement)}</TableCell><TableCell align="right">{w.counted}</TableCell>
                <TableCell align="right">{w.observerUnknown}</TableCell><TableCell align="right">{w.exceptions}</TableCell>
                <TableCell align="right">{w.cannotTell}</TableCell>
                <TableCell align="right">{w.partCounted ? `${pct(w.partAgreement)} of ${w.partCounted}` : '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {data.areas?.length > 0 && (
        <Typography variant="body2" color="text.secondary">
          Sampling per area (placeholder tiers; critical fails are always sampled; at most {data.weeklyCap} others a week):{' '}
          {data.areas.map((a) => `${a.area} ${data.tiers.find((t) => t.key === a.tier)?.label ?? a.tier} (${a.agreed}/${a.counted})`).join('; ')}
        </Typography>
      )}
    </Stack>
  )
}

function RulingRow({ item, onDone }) {
  const [form, setForm] = useState({ solved: '', failedPart: '', exception: false, exceptionReason: '' })
  const [error, setError] = useState(null)
  const [busy, setBusy] = useState(false)
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }))
  const submit = async () => {
    const r = rulingRequest(item.convNumber, form)
    if (r.error) { setError(r.error); return }
    setBusy(true)
    try {
      await postQaObserverRuling(r.body)
      setError(null)
      onDone()
    } catch (e) {
      setError(rulingErrorMessage(e))
    } finally {
      setBusy(false)
    }
  }
  return (
    <TableRow>
      <TableCell><Link href={convNumberPath(item.convNumber)} target="_blank" rel="noopener">{item.convNumber}</Link></TableCell>
      <TableCell>{item.channel}{item.intent ? `, ${item.intent}` : ''}</TableCell>
      <TableCell>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <TextField select size="small" label="Solved?" value={form.solved} onChange={set('solved')} sx={{ minWidth: 150 }}>
            {Object.entries(SOLVED_LABEL).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
          </TextField>
          {form.solved === 'no' && (
            <TextField select size="small" label="Which part failed" value={form.failedPart} onChange={set('failedPart')} sx={{ minWidth: 180 }}>
              <MenuItem value="">Not sure</MenuItem>
              {Object.entries(PART_LABEL).filter(([k]) => k !== 'unknown').map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
            </TextField>
          )}
          <FormControlLabel control={<Checkbox size="small" checked={form.exception} onChange={set('exception')} />} label="Exception" />
          {form.exception && (
            <TextField select size="small" label="Why" value={form.exceptionReason} onChange={set('exceptionReason')} sx={{ minWidth: 180 }}>
              {Object.entries(EXCEPTION_LABEL).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
            </TextField>
          )}
          <Button size="small" variant="contained" disabled={busy} onClick={submit}>Submit</Button>
        </Stack>
        {error && <Alert severity="warning" sx={{ mt: 1 }}>{error}</Alert>}
      </TableCell>
    </TableRow>
  )
}

function Sample() {
  const [data, setData] = useState(null)
  const [error, setError] = useState(null)
  const load = () => fetchQaObserver('rulings/sample').then((d) => { setData(d); setError(null) }).catch((e) => setError(e.message))
  useEffect(() => { load() }, [])
  if (error) return <Alert severity="error">Could not load the sample: {error}</Alert>
  if (!data) return <CircularProgress size={24} />
  if (!data.enabled) return <Alert severity="info">Rulings are not enabled in this environment (QA_OBSERVER_RULINGS_ENABLED).</Alert>
  const open = data.items.filter((i) => !i.mine)
  const done = data.items.filter((i) => i.mine)
  return (
    <Stack spacing={1}>
      <Typography variant="subtitle1">Rule on the sample, week of {data.week}</Typography>
      <Typography variant="body2" color="text.secondary">
        Seven days after the close: was the customer&apos;s problem solved? Open the contact to read it, then rule. The auditor&apos;s
        answer shows only after you submit, and a ruling cannot be changed.
      </Typography>
      {open.length === 0 && <Typography variant="body2">Nothing left to rule on this week.</Typography>}
      {open.length > 0 && (
        <Table size="small">
          <TableHead><TableRow><TableCell>Contact</TableCell><TableCell>Channel</TableCell><TableCell>Your ruling</TableCell></TableRow></TableHead>
          <TableBody>{open.map((i) => <RulingRow key={i.convNumber} item={i} onDone={load} />)}</TableBody>
        </Table>
      )}
      {done.length > 0 && (
        <Table size="small">
          <TableHead>
            <TableRow><TableCell>Contact</TableCell><TableCell>You</TableCell><TableCell>Auditor</TableCell><TableCell>Auditor&apos;s reasons</TableCell></TableRow>
          </TableHead>
          <TableBody>
            {done.map((i) => (
              <TableRow key={i.convNumber}>
                <TableCell><Link href={convNumberPath(i.convNumber)}>{i.convNumber}</Link></TableCell>
                <TableCell>{SOLVED_LABEL[i.mine.solved]}{i.mine.failedPart ? `, ${PART_LABEL[i.mine.failedPart]}` : ''}{i.mine.exception ? ` (exception: ${EXCEPTION_LABEL[i.mine.exceptionReason] ?? ''})` : ''}</TableCell>
                <TableCell>{SOLVED_LABEL[i.observer?.solved] ?? '—'}{i.observer?.failedPart ? `, ${PART_LABEL[i.observer.failedPart]}` : ''}</TableCell>
                <TableCell>{(i.observer?.reasons ?? []).join('; ') || '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Stack>
  )
}

function Audits() {
  const [solved, setSolved] = useState('')
  const [data, setData] = useState(null)
  const [error, setError] = useState(null)
  useEffect(() => {
    setData(null)
    fetchQaObserver('audits', { solved: solved || null, limit: 50 }).then((d) => { setData(d); setError(null) }).catch((e) => setError(e.message))
  }, [solved])
  return (
    <Stack spacing={1}>
      <Typography variant="subtitle1">Day-7 audits, newest first</Typography>
      <TextField select size="small" label="Solved" value={solved} onChange={(e) => setSolved(e.target.value)} sx={{ maxWidth: 220 }}>
        <MenuItem value="">All</MenuItem>
        {Object.entries(SOLVED_LABEL).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
      </TextField>
      {error && <Alert severity="error">Could not load the audits: {error}</Alert>}
      {!error && !data && <CircularProgress size={24} />}
      {data && data.audits.length === 0 && <Typography variant="body2">No audits yet.</Typography>}
      {data && data.audits.length > 0 && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Contact</TableCell><TableCell>Closed</TableCell><TableCell>Solved</TableCell><TableCell>Repeat</TableCell>
              <TableCell>Reopened</TableCell><TableCell>Order</TableCell><TableCell>Failed part</TableCell><TableCell>Reasons</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.audits.map((a) => (
              <TableRow key={a.conv_number}>
                <TableCell><Link href={convNumberPath(a.conv_number)}>{a.conv_number}</Link></TableCell>
                <TableCell>{when(a.closed_at)}</TableCell>
                {a.veiled
                  ? <TableCell colSpan={6}><em>In the ruling sample: hidden until you rule on it.</em></TableCell>
                  : (
                    <>
                      <TableCell>{a.solved}</TableCell><TableCell>{a.repeat_contact}{a.repeat_conv_number ? ` (${a.repeat_conv_number})` : ''}</TableCell>
                      <TableCell>{a.reopened}</TableCell><TableCell>{a.order_state}</TableCell>
                      <TableCell>{a.failed_part ? `${PART_LABEL[a.failed_part] ?? a.failed_part}${a.part_source ? ` (${a.part_source === 'model' ? `model ${a.model ?? ''}` : 'code'})` : ''}` : '—'}</TableCell>
                      <TableCell>{(a.reasons ?? []).join('; ') || '—'}</TableCell>
                    </>
                  )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Stack>
  )
}

export default function QaAuditPanel({ status }) {
  const a = status?.audit
  return (
    <Stack spacing={2}>
      {a && (
        <Typography variant="body2">
          Day-7 audit {a.enabled ? 'on' : 'off'} (rubric {a.rubricVersion}). Closed {a.windowDays} to {a.lookbackDays} days ago: {a.audited} of {a.due} contacts
          audited. Solved: {Object.entries(a.solved ?? {}).map(([k, n]) => `${k} ${n}`).join(', ') || 'none yet'}. {a.placeholder}
        </Typography>
      )}
      <Agreement />
      <Sample />
      <Audits />
    </Stack>
  )
}
